import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, CreditCard, Truck, Shield, Loader2, CheckCircle, Zap } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { fbTrack } from '../lib/facebookPixel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Checkout = () => {
  const navigate = useNavigate();
  const { cartItems, cartTotal, sessionId, clearCart } = useCart();
  const [processing, setProcessing] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState(false);
  const [orderId, setOrderId] = useState(null);
  const initiateCheckoutFired = useRef(false);

  useEffect(() => {
    if (initiateCheckoutFired.current || orderSuccess || cartItems.length === 0) return;
    initiateCheckoutFired.current = true;
    fbTrack('InitiateCheckout', {
      value: cartTotal,
      currency: 'INR',
      num_items: cartItems.reduce((n, item) => n + item.quantity, 0),
      content_ids: cartItems.map((item) => String(item.product_id ?? item.product?.id ?? '')).filter(Boolean),
    });
  }, [cartItems, cartTotal, orderSuccess]);
  
  const [form, setForm] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    shipping_address: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setProcessing(true);

    try {
      const response = await axios.post(`${API}/orders`, {
        session_id: sessionId,
        ...form
      });
      
      setOrderId(response.data.order_id);
      setOrderSuccess(true);
      fbTrack('Purchase', {
        value: cartTotal,
        currency: 'INR',
        num_items: cartItems.reduce((n, item) => n + item.quantity, 0),
        content_ids: cartItems.map((item) => String(item.product_id ?? item.product?.id ?? '')).filter(Boolean),
      });
      toast.success('Order placed successfully!');
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(error.response?.data?.detail || 'Failed to place order');
    } finally {
      setProcessing(false);
    }
  };

  // Success State
  if (orderSuccess) {
    return (
      <div data-testid="order-success" className="min-h-screen pt-[132px] pb-16 px-4 md:px-8 flex flex-col items-center justify-center">
        <div className="text-center max-w-lg">
          <div className="w-24 h-24 bg-teal-400/20 border border-teal-400 flex items-center justify-center mx-auto mb-8">
            <CheckCircle className="w-12 h-12 text-teal-400" />
          </div>
          <h1 className="font-heading text-4xl md:text-5xl font-black uppercase tracking-tighter mb-4">
            Order <span className="text-teal-400">Confirmed!</span>
          </h1>
          <p className="text-zinc-400 text-lg mb-4">
            Thank you for your SprintFit order. We&apos;ll email you about your ThermaCube shipment shortly.
          </p>
          <p className="text-zinc-500 text-sm mb-8">
            Order ID: <span className="font-mono text-teal-400">{orderId}</span>
          </p>
          
          <div className="glass-panel p-4 mb-8">
            <p className="text-yellow-400 text-sm">
              <strong>Note:</strong> Payment integration (Razorpay) will be added soon. This is a demo order.
            </p>
          </div>

          <Link to="/products">
            <Button 
              data-testid="continue-shopping-success"
              className="h-14 px-8 bg-teal-400 text-black hover:bg-white font-heading font-black uppercase tracking-widest skew-x-[-10deg]"
            >
              <span className="skew-x-[10deg]">Continue Shopping</span>
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  // Empty Cart
  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen pt-[132px] pb-16 px-4 md:px-8 flex flex-col items-center justify-center">
        <h1 className="font-heading text-3xl font-bold uppercase tracking-tight mb-4">Your cart is empty</h1>
        <Link to="/products" className="text-teal-400 hover:underline">
          Browse products
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="checkout-page" className="min-h-screen pt-[132px] pb-16 px-4 md:px-8">
      <div className="max-w-[1400px] mx-auto">
        {/* Back Link */}
        <Link 
          to="/cart" 
          className="inline-flex items-center gap-2 text-zinc-400 hover:text-white transition-colors mb-8"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Back to Cart</span>
        </Link>

        <div className="mb-10 rounded-xl border border-white/10 bg-zinc-900/40 px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex items-center gap-3 shrink-0">
            <div className="w-10 h-10 bg-teal-400 flex items-center justify-center skew-x-[-8deg]">
              <Zap className="w-5 h-5 text-black skew-x-[8deg]" />
            </div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-0.5">SprintFit</div>
              <div className="font-heading font-black uppercase tracking-wide text-white text-sm">Checkout</div>
              <div className="text-zinc-500 text-xs">ThermaCube in your cart</div>
            </div>
          </div>
          <p className="text-zinc-400 text-sm leading-relaxed sm:border-l sm:border-white/10 sm:pl-6 sm:ml-2">
            You&apos;re buying a SprintFit product (ThermaCube). Questions?{' '}
            <a href="mailto:support@sprintfit.in" className="text-teal-400 hover:underline">
              support@sprintfit.in
            </a>
          </p>
        </div>

        <h1 className="font-heading text-5xl md:text-7xl font-black uppercase tracking-tighter mb-12">
          <span className="gradient-text">Checkout</span>
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Checkout Form */}
          <div>
            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Contact Information */}
              <div className="glass-panel p-6 md:p-8">
                <h2 className="font-heading text-2xl font-bold uppercase tracking-tight mb-6 flex items-center gap-3">
                  <div className="w-8 h-8 bg-teal-400 text-black flex items-center justify-center font-heading font-bold">1</div>
                  Contact Information
                </h2>
                
                <div className="space-y-4">
                  <div>
                    <label className="font-mono text-xs uppercase tracking-widest text-zinc-400 mb-2 block">Full Name</label>
                    <Input 
                      data-testid="checkout-name"
                      value={form.customer_name}
                      onChange={(e) => setForm({...form, customer_name: e.target.value})}
                      placeholder="John Doe"
                      required
                      className="bg-zinc-900/50 border-white/10 focus:border-teal-400 rounded-none h-12 text-white placeholder:text-zinc-600"
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="font-mono text-xs uppercase tracking-widest text-zinc-400 mb-2 block">Email</label>
                      <Input 
                        data-testid="checkout-email"
                        type="email"
                        value={form.customer_email}
                        onChange={(e) => setForm({...form, customer_email: e.target.value})}
                        placeholder="john@example.com"
                        required
                        className="bg-zinc-900/50 border-white/10 focus:border-teal-400 rounded-none h-12 text-white placeholder:text-zinc-600"
                      />
                    </div>
                    <div>
                      <label className="font-mono text-xs uppercase tracking-widest text-zinc-400 mb-2 block">Phone</label>
                      <Input 
                        data-testid="checkout-phone"
                        type="tel"
                        value={form.customer_phone}
                        onChange={(e) => setForm({...form, customer_phone: e.target.value})}
                        placeholder="10-digit mobile number"
                        required
                        className="bg-zinc-900/50 border-white/10 focus:border-teal-400 rounded-none h-12 text-white placeholder:text-zinc-600"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Shipping Address */}
              <div className="glass-panel p-6 md:p-8">
                <h2 className="font-heading text-2xl font-bold uppercase tracking-tight mb-6 flex items-center gap-3">
                  <div className="w-8 h-8 bg-teal-400 text-black flex items-center justify-center font-heading font-bold">2</div>
                  Shipping Address
                </h2>
                
                <div>
                  <label className="font-mono text-xs uppercase tracking-widest text-zinc-400 mb-2 block">Full Address</label>
                  <Textarea 
                    data-testid="checkout-address"
                    value={form.shipping_address}
                    onChange={(e) => setForm({...form, shipping_address: e.target.value})}
                    placeholder="123 Main Street, Apartment 4B&#10;Mumbai, Maharashtra 400001"
                    required
                    rows={4}
                    className="bg-zinc-900/50 border-white/10 focus:border-teal-400 rounded-none text-white placeholder:text-zinc-600 resize-none"
                  />
                </div>
              </div>

              {/* Payment */}
              <div className="glass-panel p-6 md:p-8">
                <h2 className="font-heading text-2xl font-bold uppercase tracking-tight mb-6 flex items-center gap-3">
                  <div className="w-8 h-8 bg-teal-400 text-black flex items-center justify-center font-heading font-bold">3</div>
                  Payment Method
                </h2>
                
                <div className="border border-teal-400/50 p-4 bg-teal-400/5">
                  <div className="flex items-center gap-3 mb-2">
                    <CreditCard className="w-6 h-6 text-teal-400" />
                    <span className="font-heading font-bold uppercase">Razorpay</span>
                    <span className="ml-auto text-xs bg-yellow-400/20 text-yellow-400 px-2 py-1 font-mono uppercase">Coming Soon</span>
                  </div>
                  <p className="text-zinc-400 text-sm">
                    Secure payment via Razorpay. Cards, UPI, NetBanking accepted.
                  </p>
                </div>
              </div>

              {/* Submit */}
              <Button
                type="submit"
                disabled={processing}
                data-testid="place-order-btn"
                className="w-full h-16 bg-teal-400 text-black hover:bg-white font-heading font-black uppercase tracking-widest skew-x-[-10deg] text-xl group"
              >
                <span className="skew-x-[10deg] flex items-center justify-center gap-3">
                  {processing ? (
                    <>
                      <Loader2 className="w-6 h-6 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      Place Order
                      <span className="font-mono">₹{cartTotal.toLocaleString()}</span>
                    </>
                  )}
                </span>
              </Button>
            </form>
          </div>

          {/* Order Summary */}
          <div>
            <div className="glass-panel p-6 md:p-8 sticky top-28">
              <h2 className="font-heading text-2xl font-bold uppercase tracking-tight mb-6">
                Order Summary
              </h2>

              {/* Items */}
              <div className="space-y-4 mb-6 max-h-[300px] overflow-y-auto">
                {cartItems.map((item) => (
                  <div key={item.id} className="flex gap-4">
                    <div className="w-16 h-16 bg-zinc-900 flex-shrink-0">
                      <img 
                        src={item.product?.image} 
                        alt={item.product?.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-heading font-bold uppercase text-sm">
                        {item.product?.name}
                      </h4>
                      <p className="text-zinc-400 text-sm">Qty: {item.quantity}</p>
                    </div>
                    <div className="font-heading font-bold">
                      ₹{((item.product?.price || 0) * item.quantity).toLocaleString()}
                    </div>
                  </div>
                ))}
              </div>

              {/* Totals */}
              <div className="border-t border-zinc-800 pt-4 space-y-3">
                <div className="flex justify-between text-zinc-400">
                  <span>Subtotal</span>
                  <span>₹{cartTotal.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-zinc-400">
                  <span>Shipping</span>
                  <span className="text-teal-400">FREE</span>
                </div>
                <div className="flex justify-between text-zinc-400">
                  <span>Tax (18% GST)</span>
                  <span>₹{Math.round(cartTotal * 0.18).toLocaleString()}</span>
                </div>
              </div>

              <div className="border-t border-zinc-800 pt-4 mt-4">
                <div className="flex justify-between items-baseline">
                  <span className="font-heading text-xl font-bold uppercase">Total</span>
                  <span 
                    data-testid="checkout-total"
                    className="font-heading text-3xl font-black"
                  >
                    ₹{Math.round(cartTotal * 1.18).toLocaleString()}
                  </span>
                </div>
              </div>

              {/* Trust Badges */}
              <div className="grid grid-cols-3 gap-2 mt-6 pt-6 border-t border-zinc-800">
                <div className="text-center">
                  <Truck className="w-5 h-5 text-teal-400 mx-auto mb-1" />
                  <span className="text-xs text-zinc-500">Free Ship</span>
                </div>
                <div className="text-center">
                  <Shield className="w-5 h-5 text-teal-400 mx-auto mb-1" />
                  <span className="text-xs text-zinc-500">Secure</span>
                </div>
                <div className="text-center">
                  <CreditCard className="w-5 h-5 text-teal-400 mx-auto mb-1" />
                  <span className="text-xs text-zinc-500">Encrypted</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
